import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { axiosInstance } from '../../../helpers/api_helper';
import { KycVerificationStatusEnum, User } from './types';
import { handleValidDate, handleValidTime } from '../../../helpers/helpers';
import { toast } from 'react-toastify';

export const getUsers = createAsyncThunk(
  'users/getUsers',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get('/user/mlm/filter', {
      params
    });
    return response.data.data;
  }
);

export const exportUsers = createAsyncThunk(
  'users/exportUsers',
  async (params: URLSearchParams) => {
    const response = await axiosInstance.get('/user/mlm/filter/export', {
      params
    });

    try {
      if (response?.data?.data?.users) {
        const dataForExport = response?.data?.data?.users.map((u: User) => {
          return {
            id: u?.id,
            user: `${u?.name} ${u?.surname}`,
            email: u?.email,
            username: u?.username,
            rank: u?.packageRank?.name,
            sumsubStatus: u?.sumsubStatus,
            country: u?.country,
            referrerId: u?.referrerId,
            sponsorName: u?.sponsorName,
            structureSide: u?.structureSide,
            dateCreated: u?.dateCreated
              ? `${handleValidDate(u.dateCreated)}, ${handleValidTime(
                  u.dateCreated
                )}`
              : ''
          };
        });

        return dataForExport;
      }

      if (response.data.message) {
        toast.success(response.data.message);
      }
    } catch (error: any) {
      toast.error(error.response.data.error);
    }
  }
);

export const exportHeaders = [
  { label: 'ID', key: 'id' },
  { label: 'User', key: 'user' },
  { label: 'Email', key: 'email' },
  { label: 'Username', key: 'username' },
  { label: 'Rank', key: 'rank' },
  { label: 'Sponsor Id', key: 'referrerId' },
  { label: 'Sponsor Name', key: 'sponsorName' },
  { label: 'Sponsor Side', key: 'structureSide' },
  { label: 'SumSub Kyc Status', key: 'sumsubStatus' },
  { label: 'Country', key: 'country' },
  { label: 'Date Created', key: 'dateCreated' }
];

export interface UsersState {
  data: User[];
  loading: boolean;
  totalCount: number;
  dataForExport: User[];
  loadingExport: boolean;
  currentRequestId?: string;
}

const initialState = {
  data: [],
  totalCount: 0,
  loading: false,
  dataForExport: [],
  loadingExport: false,
  currentRequestId: undefined
} as UsersState;

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    removeUsersForExport(state) {
      state.dataForExport = [];
    }
    // fill in primary logic here
  },
  extraReducers: (builder) => {
    builder.addCase(getUsers.pending, (state, action) => {
      state.loading = true;
      state.currentRequestId = action.meta.requestId;
    });

    builder.addCase(getUsers.fulfilled, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.loading = false;
      state.data = action.payload.users as User[];
      state.totalCount = action.payload.count;
    });

    builder.addCase(getUsers.rejected, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.loading = false;
    });
    builder.addCase(exportUsers.pending, (state) => {
      state.loadingExport = true;
    });
    builder.addCase(exportUsers.fulfilled, (state, { payload }) => {
      state.loadingExport = false;
      state.dataForExport = payload;
    });
    builder.addCase(exportUsers.rejected, (state) => {
      state.loadingExport = false;
    });
  }
});

export const { removeUsersForExport } = usersSlice.actions;

export default usersSlice.reducer;
